import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { AutomateDataState } from 'src/app/modules/admin/profile-management/profile-detail/automate-datastate-log/automate-datastate-model';
import { Page } from 'src/app/shared/models/paging/page';
import { PagedData } from 'src/app/shared/models/paging/paged-data';
import { InquiryStageStat } from '../models/inquiry-stage-stat.model';
import { CrmProposalHistoryWidgetStore } from 'src/app/shared/stores';

/**
 * @summary Shared data contract between proposal-history UI and store.
 * @description
 * Defines the minimum API/stream set that any proposal-history store must expose
 * for grid, summary widget, and shell components.
 *
 * Business value:
 * - UI modules can stay reusable and testable
 * - store implementation can evolve without breaking view components
 *
 * @see {@link PROPOSAL_HISTORY_GRID_DATA_SOURCE}
 * @see {@link CrmProposalHistoryWidgetStore}
 */
export interface ProposalHistoryStageFilterOption {
  text: string;
  value: string | number;
}

export interface ProposalHistoryGridDataSource {
  /** Default page size when load-all mode is off. */
  readonly pageSize: number;
  /** True while summary counters/stats are being fetched. */
  readonly summaryLoading$: Observable<boolean>;
  /** True while list page data is being fetched. */
  readonly listLoading$: Observable<boolean>;
  /** Current load-all toggle state from store. */
  readonly loadAll$: Observable<boolean>;
  /** True while grid component is mounting in modal body. */
  readonly gridMounting$: Observable<boolean>;
  /** True after grid mounts successfully at least once. */
  readonly gridMounted$: Observable<boolean>;
  /** True when mount/load pipeline failed and should display retry state. */
  readonly gridLoadError$: Observable<boolean>;
  /** Summary skeleton visibility state for first-load UX. */
  readonly summarySkeletonVisible$: Observable<boolean>;
  /** Unified modal-body loading state (mounting OR list loading). */
  readonly modalBodyLoading$: Observable<boolean>;
  /** Unified modal-body error state after loading is settled. */
  readonly modalBodyError$: Observable<boolean>;
  /** Raw stage catalog options for display/filter mapping. */
  readonly stageList$: Observable<AutomateDataState[]>;
  /** Aggregated stage counters used in summary view. */
  readonly stageStats$: Observable<InquiryStageStat[]>;
  /** Derived dropdown options for stage filter in table. */
  readonly stageFilterOptions$: Observable<ProposalHistoryStageFilterOption[]>;
  /** Current page data consumed by the grid. */
  readonly pageData$: Observable<PagedData<any> | null>;

  /** Register a UI consumer to lifecycle ref-count. */
  attach(consumerId: string): void;
  /** Unregister a UI consumer from lifecycle ref-count. */
  detach(consumerId: string): void;
  /** Set active query identity (account id/email). */
  setQuery(queryKey: string): Promise<void>;
  /** Load initial state for the active query. */
  init(queryKey?: string): Promise<void>;
  /** Reload summary and first page for the active query. */
  refreshSummary(): Promise<void>;
  /** Load target page/filter/sort from grid request. */
  changePage(page: Page): Promise<void>;
  /** Toggle load-all mode for next list requests. */
  setLoadAll(loadAll: boolean): void;
  /** Mark grid mount start. */
  beginGridMount(): void;
  /** Mark grid mount success. */
  completeGridMount(): void;
  /** Mark grid mount failure. */
  failGridMount(): void;
  /** Reset transient mount flags. */
  resetGridMountState(): void;
}

export const PROPOSAL_HISTORY_GRID_DATA_SOURCE = new InjectionToken<ProposalHistoryGridDataSource>(
  'PROPOSAL_HISTORY_GRID_DATA_SOURCE',
);
